import type { CatalogPriceLine, CatalogPricesProposal } from './catalogPrices';
import type { Draft } from './drafts';
import { DEFAULT_NATURE, visibleCategory } from './finance';
import { normalizeText } from './importer';
import { MARGEN_CUADRE } from './escanerLineas';
import { buscarDuplicado, desgloseCuadra, totalDeFactura, type ExpenseDraft } from './receipts';
import { taxIdComparable } from './suppliers';
import type { ExpenseEntry, Ingredient, PurchaseDocumentType, Supplier } from './types';
import type { ExpenseInput } from './validation';

/**
 * Bloque S · EL ESCÁNER ÚNICO (decidido con Alex el 11 oct, revisado en dev
 * el 12 oct 2026). Una factura se sube una vez —desde Gastos o desde
 * Ingredientes— y Alexia guarda el gasto Y actualiza los precios. Lo que
 * queda por decidir no bloquea: espera en «Por revisar», UNA fila por
 * factura (antes eran dos borradores del mismo archivo: el gasto y sus
 * precios).
 *
 * ⚠️ FUNDAMENTAL · LA IA NO SE PUEDE EQUIVOCAR. «Todo claro» y «Guardar las
 * N» guardan facturas sin que nadie mire cada una: un solo error y el dueño
 * deja de fiarse del escáner para siempre. Por eso «Todo claro» exige TODAS
 * las condiciones de la nota roja de Alex, y ante la mínima duda la factura
 * va a revisar. Un verde equivocado es peor que diez ámbar de más.
 */

export const REGLAS_ESCANER = {
  /** Diferencia máxima en las cuentas de la factura (suma de líneas, total). */
  margenCuadre: MARGEN_CUADRE,
  /** Ningún precio sube ni baja más de esto respecto al anterior. */
  cambioMaxPct: 15,
  /** Ni futura ni de hace más de estos días. */
  diasMaximos: 60,
  /** Facturas del proveedor ya guardadas y revisadas a mano. */
  facturasRevisadasMinimas: 3,
  /** «Guardar las N» no sale los primeros meses de cada cuenta. */
  mesesSinGuardarLasN: 2,
  /** «Guardada sin revisar» se ve estos días en la lista de Gastos. */
  diasSinRevisar: 7,
  /** Si se corrigen o deshacen más de este tanto, «Guardar las N» se apaga. */
  tasaMaximaCorrecciones: 0.01,
  /** Solo se avisa de los escaneos que quedan cuando son menos de estos. */
  avisoEscaneos: 20,
  /** Cambios de menos de este % cuentan como «Precio igual». */
  umbralIgualPct: 1,
} as const;

/** Por qué una factura NO es «Todo claro». Uno por condición de la nota roja. */
export type MotivoRevisar =
  | 'nota'
  | 'avisos'
  | 'cuentas'
  | 'proveedor-nuevo'
  | 'proveedor-sin-id-fiscal'
  | 'proveedor-pocas-facturas'
  | 'producto-dudoso'
  | 'precio-sin-comprobar'
  | 'presentacion'
  | 'precio-cambia'
  | 'repetida'
  | 'fecha'
  | 'confianza'
  | 'paginas';

export interface ContextoEscaner {
  /** Hoy, YYYY-MM-DD. */
  hoy: string;
  suppliers: Array<Pick<Supplier, 'supplierId' | 'name' | 'taxId' | 'creditDays' | 'archived'>>;
  expenses: Array<Pick<ExpenseEntry, 'entryId' | 'supplierId' | 'documentType' | 'date' | 'dueDate' | 'netAmount' | 'vatPct' | 'concept' | 'invoiceNumber' | 'guardadaSinRevisar' | 'reconciledInvoiceId' | 'invoiceStatus'>>;
  ingredients: Array<Pick<Ingredient, 'ingredientId' | 'supplierId'>>;
}

/* ------------------------------------------------------------------ */
/* Productos                                                           */
/* ------------------------------------------------------------------ */

/**
 * ¿Esta línea pide algo al usuario? Lo dudoso va en ámbar y no se aplica
 * solo: «¿Es este?», «Nuevo», precio sin comprobar (cantidad × unitario ≠
 * importe, triple o un tercio) o una unidad que no casa con la del
 * ingrediente.
 */
export function lineaPorDecidir(line: CatalogPriceLine): boolean {
  if (line.action === 'ignore') return false;
  if (line.action !== 'update' || line.match.kind !== 'exact') return true;
  if (line.duda) return true;
  if (!line.unitPrice) return true;
  return !line.comparison;
}

export function lineasPorDecidir(prices: CatalogPricesProposal | undefined): number {
  return (prices?.lines ?? []).filter(lineaPorDecidir).length;
}

/**
 * Regla 3 de la nota roja: la línea es un ingrediente que el usuario YA
 * confirmó con ese mismo texto. Vale un alias recordado («PAN CAJA BCO GDE» =
 * Pan de caja blanco) o el nombre exacto de un ingrediente de ESE proveedor
 * (lo creó «Crearlo» desde una factura suya). Un parecido, nunca.
 */
function lineaConfirmadaAntes(line: CatalogPriceLine, supplierId: string | undefined, ingredients: ContextoEscaner['ingredients']): boolean {
  if (line.action === 'ignore') return line.ignoreReason === 'recordado';
  if (line.match.kind !== 'exact') return false;
  const c = line.match.candidates[0];
  if (c.alias) return true;
  const ing = ingredients.find((i) => i.ingredientId === c.ingredientId);
  return Boolean(supplierId && ing?.supplierId === supplierId);
}

/* ------------------------------------------------------------------ */
/* «Todo claro»                                                        */
/* ------------------------------------------------------------------ */

const diasEntre = (a: string, b: string): number =>
  Math.round((Date.parse(`${b}T00:00:00Z`) - Date.parse(`${a}T00:00:00Z`)) / 86_400_000);

export function facturasRevisadasDe(supplierId: string, expenses: ContextoEscaner['expenses']): number {
  return expenses.filter((e) => e.supplierId === supplierId && (e.documentType ?? 'factura') === 'factura' && !e.guardadaSinRevisar).length;
}

/** Regla 1: las cuentas cuadran solas (líneas = subtotal; subtotal − descuento + IVA = total). */
export function cuentasCuadran(d: Pick<ExpenseDraft, 'sumaLineas' | 'subtotalBeforeDiscount' | 'discountAmount' | 'netAmount' | 'taxAmount' | 'invoiceTotal'>): boolean {
  const m = REGLAS_ESCANER.margenCuadre + 1e-9;
  if (d.sumaLineas == null || d.taxAmount == null || d.invoiceTotal == null) return false;
  const subtotal = d.subtotalBeforeDiscount ?? d.netAmount;
  if (Math.abs(d.sumaLineas - subtotal) > m) return false;
  if (Math.abs(subtotal - (d.discountAmount ?? 0) - d.netAmount) > m) return false;
  return Math.abs(d.netAmount + d.taxAmount - d.invoiceTotal) <= m;
}

/**
 * Todo lo que impide que una factura sea «Todo claro». Vacío = todo claro.
 * Son TODAS las condiciones de la nota roja de Alex (11 oct). La 8
 * («confianza máxima en todos los campos») se adapta a lo que da el lector,
 * como se acordó: confianza alta en proveedor, fecha, importe y categoría,
 * identificación fiscal y número leídos con seguridad, y para las líneas,
 * solo lo ya confirmado antes.
 */
export function motivosParaRevisar(
  proposal: ExpenseDraft,
  documentType: PurchaseDocumentType | undefined,
  prices: CatalogPricesProposal | undefined,
  ctx: ContextoEscaner,
  opciones: { requiresReview?: boolean } = {},
): MotivoRevisar[] {
  const out: MotivoRevisar[] = [];
  const add = (m: MotivoRevisar) => { if (!out.includes(m)) out.push(m); };

  if (proposal.esNota || documentType === 'albaran') add('nota');
  if (proposal.avisos.length > 0 || opciones.requiresReview) add('avisos');
  if (!cuentasCuadran(proposal)) add('cuentas');

  // 2. Proveedor seguro: por su identificación fiscal y con 3 facturas revisadas.
  const supplier = proposal.supplierId ? ctx.suppliers.find((s) => s.supplierId === proposal.supplierId) : undefined;
  if (!supplier) add('proveedor-nuevo');
  else {
    const guardado = taxIdComparable(supplier.taxId);
    if (!guardado || guardado !== taxIdComparable(proposal.supplierTaxId)) add('proveedor-sin-id-fiscal');
    if (facturasRevisadasDe(supplier.supplierId, ctx.expenses) < REGLAS_ESCANER.facturasRevisadasMinimas) add('proveedor-pocas-facturas');
  }

  // 3, 4 y 5. Productos seguros, presentación igual, precios sin sorpresas.
  for (const line of prices?.lines ?? []) {
    if (!lineaConfirmadaAntes(line, proposal.supplierId, ctx.ingredients)) add('producto-dudoso');
    if (line.action === 'ignore') continue;
    if (line.duda || !line.unitPrice) add('precio-sin-comprobar');
    if (line.presentacionIgual !== true) add('presentacion');
    if (!line.comparison || Math.abs(line.comparison.changePct) > REGLAS_ESCANER.cambioMaxPct) add('precio-cambia');
  }

  // 6. No es repetida (la de cuando se leyó y contra lo guardado desde entonces).
  if (proposal.duplicado || buscarDuplicado(
    { supplierId: proposal.supplierId, invoiceNumber: proposal.invoiceNumber, date: proposal.date, netAmount: proposal.netAmount },
    ctx.expenses,
    documentType ?? 'factura',
  )) add('repetida');

  // 7. Fecha razonable.
  const dias = diasEntre(proposal.date, ctx.hoy);
  if (!(dias >= 0 && dias <= REGLAS_ESCANER.diasMaximos)) add('fecha');

  // 8. Confianza del lector.
  const c = proposal.confianza;
  if (c.proveedor !== 'alta' || c.fecha !== 'alta' || c.importe !== 'alta' || c.categoria !== 'alta' || !proposal.invoiceIdentityReliable) add('confianza');

  // 9. Una sola página.
  if (proposal.paginas !== 1) add('paginas');
  return out;
}

/* ------------------------------------------------------------------ */
/* «Por revisar»: una fila por factura                                 */
/* ------------------------------------------------------------------ */

export type FaltaPorRevisar = 'repetida' | 'productos' | 'juntar-notas' | 'proveedor-nuevo' | 'datos' | 'todo-claro';

export interface FilaPorRevisar {
  /** Clave de la fila: el borrador del gasto o, si ya se guardó, el de los precios. */
  id: string;
  expenseDraftId?: string;
  pricesDraftId?: string;
  proveedor: string;
  supplierId?: string;
  /** Total con IVA (lo que dice la factura). */
  total: number;
  fecha: string;
  origen: Draft['source'];
  falta: FaltaPorRevisar;
  /** Productos que piden un toque. */
  porDecidir: number;
  /** Notas de ese proveedor sin factura (solo si la fila es una factura). */
  notasSinFactura: number;
  /** El gasto ya está guardado: solo quedan productos. */
  gastoGuardado: boolean;
  esNota: boolean;
  todoClaro: boolean;
  motivos: MotivoRevisar[];
  createdAt: string;
}

type DraftGasto = Extract<Draft, { kind: 'expense' }>;
type DraftPrecios = Extract<Draft, { kind: 'catalog-prices' }>;

export function notasSinFacturaDe(supplierId: string | undefined, expenses: ContextoEscaner['expenses']): number {
  if (!supplierId) return 0;
  return expenses.filter((e) => e.supplierId === supplierId && e.documentType === 'albaran' && !e.reconciledInvoiceId).length;
}

/**
 * Junta los borradores de un mismo archivo (gasto + precios) en una fila por
 * factura. Los precios sueltos de una factura cuyo gasto ya se guardó son su
 * propia fila («2 productos por decidir»). Primero lo más nuevo.
 */
export function filasPorRevisar(drafts: Draft[], ctx: ContextoEscaner): FilaPorRevisar[] {
  const pendientes = drafts.filter((d) => d.status === 'pending');
  const gastos = pendientes.filter((d): d is DraftGasto => d.kind === 'expense');
  const precios = pendientes.filter((d): d is DraftPrecios => d.kind === 'catalog-prices');
  const usados = new Set<string>();
  const filas: FilaPorRevisar[] = [];

  for (const g of gastos) {
    const p = g.inputKey ? precios.find((x) => x.inputKey === g.inputKey && !usados.has(x.id)) : undefined;
    if (p) usados.add(p.id);
    const proposal = g.payload.proposal;
    const motivos = motivosParaRevisar(proposal, g.payload.documentType, p?.payload, ctx, { requiresReview: g.payload.requiresReview });
    const porDecidir = lineasPorDecidir(p?.payload);
    const esNota = Boolean(proposal.esNota || g.payload.documentType === 'albaran');
    const notas = esNota ? 0 : notasSinFacturaDe(proposal.supplierId, ctx.expenses);
    const falta: FaltaPorRevisar = motivos.includes('repetida') ? 'repetida'
      : porDecidir > 0 ? 'productos'
      : notas > 0 ? 'juntar-notas'
      : motivos.length === 0 ? 'todo-claro'
      : !proposal.supplierId ? 'proveedor-nuevo'
      : 'datos';
    filas.push({
      id: g.id,
      expenseDraftId: g.id,
      ...(p ? { pricesDraftId: p.id } : {}),
      proveedor: ctx.suppliers.find((s) => s.supplierId === proposal.supplierId)?.name ?? proposal.supplierName ?? proposal.concept,
      ...(proposal.supplierId ? { supplierId: proposal.supplierId } : {}),
      total: totalDeFactura(proposal),
      fecha: proposal.date,
      origen: g.source,
      falta,
      porDecidir,
      notasSinFactura: notas,
      gastoGuardado: false,
      esNota,
      todoClaro: motivos.length === 0 && notas === 0,
      motivos,
      createdAt: g.createdAt,
    });
  }

  for (const p of precios) {
    if (usados.has(p.id)) continue;
    const porDecidir = lineasPorDecidir(p.payload) || p.payload.lines.filter((l) => l.action !== 'ignore').length;
    if (!porDecidir) continue;
    const f = p.payload.factura;
    filas.push({
      id: p.id,
      pricesDraftId: p.id,
      proveedor: (f?.supplierId ? ctx.suppliers.find((s) => s.supplierId === f.supplierId)?.name : undefined) ?? f?.proveedor ?? 'Precios de una factura',
      ...(f?.supplierId ? { supplierId: f.supplierId } : {}),
      total: f?.total ?? 0,
      fecha: f?.fecha ?? p.createdAt.slice(0, 10),
      origen: p.source,
      falta: 'productos',
      porDecidir,
      notasSinFactura: 0,
      gastoGuardado: true,
      esNota: Boolean(f?.esNota),
      todoClaro: false,
      motivos: ['producto-dudoso'],
      createdAt: p.createdAt,
    });
  }
  return filas.sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}

/* ------------------------------------------------------------------ */
/* «Guardar las N»                                                     */
/* ------------------------------------------------------------------ */

/** Métrica por cuenta: cuántas «todo claro» se guardaron solas y cuántas se corrigieron o deshicieron. */
export interface EstadisticasEscaner {
  guardadasSinRevisar: number;
  corregidas: number;
  deshechas: number;
}

export const ESTADISTICAS_VACIAS: EstadisticasEscaner = { guardadasSinRevisar: 0, corregidas: 0, deshechas: 0 };

const sumarMeses = (fecha: string, meses: number): string => {
  const d = new Date(`${fecha.slice(0, 10)}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + meses);
  return d.toISOString().slice(0, 10);
};

/**
 * ¿Sale «Guardar las N»? No los dos primeros meses de la cuenta (el usuario
 * revisa todo y Alexia aprende sus proveedores) ni si más del 1 % de las que
 * se guardaron solas se corrigieron o deshicieron (se apaga hasta arreglarlo).
 */
export function guardarLasNDisponible(
  cuentaCreada: string | undefined,
  hoy: string,
  stats: EstadisticasEscaner = ESTADISTICAS_VACIAS,
): { visible: boolean; motivo?: 'cuenta-nueva' | 'correcciones' } {
  if (!cuentaCreada || hoy < sumarMeses(cuentaCreada, REGLAS_ESCANER.mesesSinGuardarLasN)) return { visible: false, motivo: 'cuenta-nueva' };
  const fallos = stats.corregidas + stats.deshechas;
  if (stats.guardadasSinRevisar > 0 && fallos / stats.guardadasSinRevisar > REGLAS_ESCANER.tasaMaximaCorrecciones) return { visible: false, motivo: 'correcciones' };
  return { visible: true };
}

/** ¿Se enseña todavía «Guardada sin revisar»? Siete días desde que se guardó. */
export function sinRevisarVigente(e: Pick<ExpenseEntry, 'guardadaSinRevisar'>, hoy: string): boolean {
  if (!e.guardadaSinRevisar) return false;
  const dias = diasEntre(e.guardadaSinRevisar.slice(0, 10), hoy);
  return dias >= 0 && dias < REGLAS_ESCANER.diasSinRevisar;
}

/* ------------------------------------------------------------------ */
/* Lo que se guarda                                                    */
/* ------------------------------------------------------------------ */

/**
 * Cuándo vence: lo que dice la factura; si no, los días de crédito del
 * proveedor; si no, los mismos días que su última factura a crédito («el
 * proveedor recuerda»). Sin nada de eso, no se inventa.
 */
export function vencimientoDe(
  proposal: Pick<ExpenseDraft, 'date' | 'dueDate' | 'supplierId'>,
  ctx: Pick<ContextoEscaner, 'suppliers' | 'expenses'>,
): { dueDate?: string; dias?: number; de?: 'factura' | 'proveedor' | 'ultima' } {
  const sumar = (dias: number) => new Date(Date.parse(`${proposal.date}T00:00:00Z`) + dias * 86_400_000).toISOString().slice(0, 10);
  if (proposal.dueDate && proposal.dueDate > proposal.date) return { dueDate: proposal.dueDate, dias: diasEntre(proposal.date, proposal.dueDate), de: 'factura' };
  const s = proposal.supplierId ? ctx.suppliers.find((x) => x.supplierId === proposal.supplierId) : undefined;
  if (s?.creditDays != null && s.creditDays > 0) return { dueDate: sumar(s.creditDays), dias: s.creditDays, de: 'proveedor' };
  const ultima = proposal.supplierId
    ? ctx.expenses
      .filter((e) => e.supplierId === proposal.supplierId && e.dueDate && e.dueDate > e.date)
      .sort((a, b) => b.date.localeCompare(a.date))[0]
    : undefined;
  if (ultima?.dueDate) {
    const dias = diasEntre(ultima.date, ultima.dueDate);
    return { dueDate: sumar(dias), dias, de: 'ultima' };
  }
  return {};
}

/**
 * El gasto que sale del borrador del escáner. Fijo o variable lo decide la
 * categoría (nunca se pregunta). Una nota cuenta el total y espera factura.
 */
export function gastoDesdeEscaneo(
  proposal: ExpenseDraft,
  opciones: { documentType?: PurchaseDocumentType; paid: boolean; dueDate?: string; attachmentKey?: string; concept?: string },
): ExpenseInput {
  const esNota = Boolean(proposal.esNota || opciones.documentType === 'albaran');
  const paid = opciones.paid;
  // Una nota no lleva IVA: cuenta el total. Base + IVA (0) = total, siempre.
  const totalNota = esNota ? totalDeFactura(proposal) : undefined;
  return {
    documentType: esNota ? 'albaran' : 'factura',
    date: proposal.date,
    ...(paid || !opciones.dueDate ? {} : { dueDate: opciones.dueDate }),
    concept: (opciones.concept ?? proposal.concept).trim().slice(0, 160) || 'Gasto escaneado',
    ...(proposal.supplierId ? { supplierId: proposal.supplierId } : {}),
    ...(proposal.invoiceNumber ? { invoiceNumber: proposal.invoiceNumber.slice(0, 40) } : {}),
    category: proposal.category,
    nature: DEFAULT_NATURE[visibleCategory(proposal.category)] ?? proposal.nature,
    netAmount: totalNota ?? proposal.netAmount,
    vatPct: esNota ? 0 : proposal.vatPct,
    ...(proposal.subtotalBeforeDiscount != null && !esNota ? { subtotalBeforeDiscount: proposal.subtotalBeforeDiscount } : {}),
    ...(proposal.discountAmount != null && !esNota ? { discountAmount: proposal.discountAmount } : {}),
    ...(proposal.taxAmount != null ? { taxAmount: esNota ? 0 : proposal.taxAmount } : {}),
    ...(totalNota != null ? { invoiceTotal: totalNota } : proposal.invoiceTotal != null ? { invoiceTotal: proposal.invoiceTotal } : {}),
    ...(proposal.taxMode && !esNota ? { taxMode: proposal.taxMode } : {}),
    ...(desgloseCuadra(proposal.vatBreakdown, proposal.netAmount) && !esNota ? { vatBreakdown: proposal.vatBreakdown } : {}),
    paymentType: paid || !opciones.dueDate ? 'contado' : 'credito',
    paid,
    invoiceStatus: esNota ? 'pendiente' : 'facturado',
    ...(opciones.attachmentKey ? { attachmentKey: opciones.attachmentKey } : {}),
    ...(proposal.vatNote && !esNota ? { notes: proposal.vatNote } : {}),
  } as ExpenseInput;
}

/**
 * Nombre limpio para un proveedor nuevo: «PANADERIA LA ESPIGA, S.A. DE C.V.»
 * → «Panaderia La Espiga». Sin forma jurídica y, si venía en mayúsculas, en
 * tipo título. Lo que el usuario escribió con mayúsculas y minúsculas se
 * respeta.
 */
const FORMAS_FINALES = new Set(['sa', 'sl', 'slu', 'sll', 'sc', 'cv', 'de', 'rl', 'sapi', 'sab', 'srl', 'sas', 'spa', 'ltda', 'ltd', 'inc', 'llc', 'corp', 'cb', 'scp', 'coop', 'gmbh', 'eirl', 'sacv', 'sadecv', 'y']);
const MENORES = new Set(['de', 'del', 'y', 'e', 'o', 'u', 'en']);
export function nombreProveedorLimpio(raw: string): string {
  const palabras = raw.replace(/[,;]+/g, ' ').split(/\s+/).filter(Boolean);
  const clave = (w: string) => normalizeText(w.replace(/\./g, ''));
  while (palabras.length > 1 && (FORMAS_FINALES.has(clave(palabras[palabras.length - 1])) || clave(palabras[palabras.length - 1]) === '')) palabras.pop();
  const texto = palabras.join(' ').replace(/[.,\s]+$/, '');
  if (texto !== texto.toUpperCase()) return texto;
  return texto.toLowerCase().split(' ').map((w, i) => (i > 0 && MENORES.has(w) ? w : w.charAt(0).toUpperCase() + w.slice(1))).join(' ');
}

/** Nombre de ingrediente a partir del texto de la factura: «BARRA RUSTICA» → «Barra rustica». */
export function nombreIngredienteLimpio(raw: string): string {
  const t = raw.replace(/\s+/g, ' ').trim();
  if (!t || t !== t.toUpperCase()) return t;
  const bajo = t.toLowerCase();
  return bajo.charAt(0).toUpperCase() + bajo.slice(1);
}

/**
 * Precio nuevo de la presentación que ya tiene el ingrediente («Paquete de 6 ·
 * antes 4,00 → ahora 4,20»): se conserva cómo lo compras y solo cambia el
 * precio, en la misma proporción que el de la factura.
 */
export function precioDePresentacion(netPriceActual: number, comparison: { previous: number; current: number }): number {
  return Math.round(netPriceActual * (comparison.current / comparison.previous) * 10_000) / 10_000;
}
